import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLanguage } from "@/lib/language-context";
import { useApp } from "@/lib/app-context";
import { visaService, type VisaApplicationStepData, type VisaApplicationDetail } from "@/lib/visa-service";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { indianPorts, PHONE_COUNTRY_CODES, educationLevels, religions, EBUSINESS_PURPOSE_TO_DOC_TYPES, EBUSINESS_PURPOSE_SLUGS } from "./constants";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Camera,
  Upload,
  Check,
  ArrowLeft,
  ArrowRight,
  Loader2,
  FileText,
  Plane,
  Users,
  GraduationCap,
  Globe,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Clock,
  Copy,
  Briefcase,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { CameraScanner } from "./camera-scanner";

// Type for Capacitor document scanner plugin
interface DocumentScannerPlugin {
  scanDocument: (options?: {
    maxNumDocuments?: number;
    responseType?: "base64" | "imageFilePath";
  }) => Promise<{ scannedImages: string[] }>;
}

// Check if running in Capacitor native environment
const isCapacitor = typeof window !== "undefined" && !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();

/** Build E.164 phone number for Django (e.g. +94771234567). */
function toE164(countryCode: string, localNumber: string): string {
  const countryDigits = countryCode.replace(/\D/g, "");
  let localDigits = localNumber.replace(/\D/g, "");
  if (localDigits.startsWith("0")) localDigits = localDigits.slice(1);
  if (!localDigits) return "";
  return `+${countryDigits}${localDigits}`;
}

/** Parse E.164 phone (e.g. +94771234567) to country code and local number. */
function parseE164(e164: string): { countryCode: string; localNumber: string } {
  const digits = e164.replace(/\D/g, "");
  if (!digits.length) return { countryCode: "+94", localNumber: "" };
  for (const { value } of PHONE_COUNTRY_CODES) {
    const codeDigits = value.replace(/\D/g, "");
    if (digits.startsWith(codeDigits)) {
      return {
        countryCode: value,
        localNumber: digits.slice(codeDigits.length).replace(/^0+/, "") || "",
      };
    }
  }
  return { countryCode: "+94", localNumber: digits };
}


// Form data type
interface VisaApplicationForm {
  // Step 0: Visa Type Selection
  visaType: "30_days" | "1_year" | "ebusiness_1_year" | "";

  // Step 1: Start Application
  fullName: string;
  phoneCountryCode: string;
  phoneNumber: string; // local number only (no country code)
  email: string;
  /** eBusiness only: purpose of travel (slug e.g. SET_UP_BUSINESS_VENTURE) */
  businessTravelPurpose: string;

  // Step 2: Passport Upload
  passportImage: string | null;

  // Step 3: Photograph Upload
  photographImage: string | null;
  /** eBusiness only: document_type -> uploaded URL */
  ebusinessDocuments: Record<string, string>;

  // Step 4: Travel Details
  portOfArrival: string;
  expectedArrivalDate: string;
  placesIntendedToVisit: string;

  // Step 5: Personal & Family Details
  fatherFullName: string;
  fatherPlaceOfBirth: string;
  motherFullName: string;
  motherPlaceOfBirth: string;
  maritalStatus: "single" | "married" | "other" | "";
  spouseFullName: string;
  spouseNationality: string;
  spousePlaceOfBirth: string;

  // Step 6: Address, Education & Religion
  residentialAddress: string;
  educationalQualifications: string;
  religion: string;
  /** eBusiness only: qualification acquired from college/university (institute name, max 100 chars) */
  educationalQualificationInstitute: string;

  // Step 7: Travel History
  visitedIndiaBefore: boolean | null;
  countriesVisited: string;
  /** Optional: previous visa ID number */
  previousVisaId: string;
  visitedSAARCCountries: boolean | null;
  historyOfOverstaying: boolean | null;
  overstayExplanation: string;
  previousVisaRejections: boolean | null;
  visaRejectionExplanation: string;
  refusedEntry: boolean | null;
  refusedEntryExplanation: string;
  arrestedConvicted: boolean | null;
  traffickingCrimes: boolean | null;
  cyberTerrorViolence: boolean | null;
  glorifyTerrorism: boolean | null;
  soughtAsylum: boolean | null;

  // Step 8: Declaration
  declarationAccepted: boolean;
}

const initialFormData: VisaApplicationForm = {
  fullName: "",
  phoneCountryCode: "+94",
  phoneNumber: "",
  email: "",
  visaType: "",
  businessTravelPurpose: "",
  passportImage: null,
  photographImage: null,
  ebusinessDocuments: {},
  portOfArrival: "",
  expectedArrivalDate: "",
  placesIntendedToVisit: "",
  fatherFullName: "",
  fatherPlaceOfBirth: "",
  motherFullName: "",
  motherPlaceOfBirth: "",
  maritalStatus: "",
  spouseFullName: "",
  spouseNationality: "",
  spousePlaceOfBirth: "",
  residentialAddress: "",
  educationalQualifications: "",
  religion: "",
  educationalQualificationInstitute: "",
  visitedIndiaBefore: null,
  countriesVisited: "",
  previousVisaId: "",
  visitedSAARCCountries: null,
  historyOfOverstaying: null,
  overstayExplanation: "",
  previousVisaRejections: null,
  visaRejectionExplanation: "",
  refusedEntry: null,
  refusedEntryExplanation: "",
  arrestedConvicted: null,
  traffickingCrimes: null,
  cyberTerrorViolence: null,
  glorifyTerrorism: null,
  soughtAsylum: null,
  declarationAccepted: false,
};

const STATUS_TO_NEXT_STEP: Record<string, number> = {
  draft: 2,
  step2_completed: 3,
  step3_completed: 4,
  step4_completed: 5,
  step5_completed: 6,
  step6_completed: 7,
  step7_completed: 8,
};

function applicationToFormData(app: VisaApplicationDetail): VisaApplicationForm {
  const phone = parseE164(app.phone_number || "");
  const expectedArrival =
    app.expected_arrival_date == null
      ? ""
      : typeof app.expected_arrival_date === "string"
        ? app.expected_arrival_date
        : String(app.expected_arrival_date);
  return {
    visaType: (app.visa_type as VisaApplicationForm["visaType"]) || "",
    fullName: app.full_name ?? "",
    phoneCountryCode: phone.countryCode,
    phoneNumber: phone.localNumber,
    email: app.email ?? "",
    businessTravelPurpose: app.business_travel_purpose ?? "",
    passportImage: app.passport_image ?? null,
    photographImage: app.photograph_image ?? null,
    ebusinessDocuments: app.ebusiness_documents ?? {},
    portOfArrival: app.port_of_arrival ?? "",
    expectedArrivalDate: expectedArrival,
    placesIntendedToVisit: app.places_to_visit ?? "",
    fatherFullName: app.father_full_name ?? "",
    fatherPlaceOfBirth: app.father_place_of_birth ?? "",
    motherFullName: app.mother_full_name ?? "",
    motherPlaceOfBirth: app.mother_place_of_birth ?? "",
    maritalStatus: (app.marital_status as VisaApplicationForm["maritalStatus"]) ?? "",
    spouseFullName: app.spouse_full_name ?? "",
    spouseNationality: app.spouse_nationality ?? "",
    spousePlaceOfBirth: app.spouse_place_of_birth ?? "",
    residentialAddress: app.residential_address ?? "",
    educationalQualifications: app.educational_qualifications ?? "",
    religion: app.religion ?? "",
    educationalQualificationInstitute: app.educational_qualification_institute ?? "",
    countriesVisited: app.cities_visited_in_india ?? "",
    visitedIndiaBefore: app.visited_india_before ?? null,
    previousVisaId: app.previous_visa_id ?? "",
    visitedSAARCCountries: app.visited_saarc_countries ?? null,
    historyOfOverstaying: app.history_of_overstaying ?? null,
    overstayExplanation: app.overstay_explanation ?? "",
    previousVisaRejections: app.previous_visa_rejections ?? null,
    visaRejectionExplanation: app.visa_rejection_explanation ?? "",
    refusedEntry: app.refused_entry_any_country ?? null,
    refusedEntryExplanation: app.refused_entry_explanation ?? "",
    arrestedConvicted: app.arrested_convicted ?? null,
    traffickingCrimes: app.trafficking_crimes ?? null,
    cyberTerrorViolence: app.cyber_terror_violence ?? null,
    glorifyTerrorism: app.glorify_terrorism ?? null,
    soughtAsylum: app.sought_asylum ?? null,
    declarationAccepted: !!app.declaration_accepted,
  };
}

export function PassportUpload() {
  const { t } = useLanguage();
  const { setCurrentScreen, setApplicationId, setVisaType, applicationId: contextApplicationId, resumeApplicationData, setResumeApplicationData } = useApp();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<VisaApplicationForm>(initialFormData);
  const [generatedApplicationId, setGeneratedApplicationId] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasDocumentScanner, setHasDocumentScanner] = useState(false);
  const [showFullScreenScanner, setShowFullScreenScanner] = useState(false);
  const [currentUploadType, setCurrentUploadType] = useState<"passport" | "photograph">("passport");
  const [resumeLoaded, setResumeLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [purposePopupOpen, setPurposePopupOpen] = useState(false);
  const [purposePopupMessage, setPurposePopupMessage] = useState("");

  const passportInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ebusinessFileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const isEbusiness = formData.visaType === "ebusiness_1_year";
  const totalSteps = isEbusiness ? 10 : 9;
  /** Backend step (2-8) for saveStep; undefined when current step is eBusiness docs only (no save). */
  const getBackendStep = (step: number) =>
    isEbusiness && step === 4 ? undefined : (isEbusiness && step > 4 ? step - 1 : step);
  /** Required document types for current eBusiness purpose */
  const requiredEbusinessDocTypes = isEbusiness && formData.businessTravelPurpose
    ? (EBUSINESS_PURPOSE_TO_DOC_TYPES[formData.businessTravelPurpose] ?? [])
    : [];

  // Resume: load application when context has applicationId (from Continue Application)
  // Use cached data from context (always provided by Continue Application flow)
  useEffect(() => {
    if (!contextApplicationId || generatedApplicationId || resumeLoaded) return;

    // Use cached data from Continue Application flow
    if (resumeApplicationData && resumeApplicationData.application_number === contextApplicationId) {
      setFormData(applicationToFormData(resumeApplicationData));
      setGeneratedApplicationId(resumeApplicationData.application_number);
      const nextStep = STATUS_TO_NEXT_STEP[resumeApplicationData.status] ?? 2;
      setCurrentStep(nextStep);
      setResumeLoaded(true);
      // Clear cached data after using it
      setResumeApplicationData(null);
    }
  }, [contextApplicationId, generatedApplicationId, resumeLoaded, resumeApplicationData, setResumeApplicationData]);

  // Detect Capacitor document scanner
  useEffect(() => {
    const checkDocumentScanner = async () => {
      if (isCapacitor) {
        try {
          const { DocumentScanner } = await import("@capgo/capacitor-document-scanner") as { DocumentScanner: DocumentScannerPlugin };
          if (DocumentScanner) {
            setHasDocumentScanner(true);
          }
        } catch {
          setHasDocumentScanner(false);
        }
      }
    };

    checkDocumentScanner();
  }, []);

  // Note: Application ID is generated by backend upon submission
  // No need to generate temporary ID on frontend

  // Update form data
  const updateFormData = (field: keyof VisaApplicationForm, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Use Capacitor Document Scanner
  const startDocumentScanner = useCallback(async (uploadType: "passport" | "photograph") => {
    if (!isCapacitor) return;

    setIsProcessing(true);
    try {
      const { DocumentScanner } = await import("@capgo/capacitor-document-scanner") as { DocumentScanner: DocumentScannerPlugin };
      const result = await DocumentScanner.scanDocument({
        maxNumDocuments: 1,
        responseType: "base64",
      });

      if (result.scannedImages && result.scannedImages.length > 0) {
        const imageData = result.scannedImages[0].startsWith("data:")
          ? result.scannedImages[0]
          : `data:image/jpeg;base64,${result.scannedImages[0]}`;

        // Upload to S3 and get URL
        const response = await visaService.uploadImage(imageData, uploadType, generatedApplicationId || undefined);

        // Store S3 URL in form data
        if (uploadType === "passport") {
          updateFormData("passportImage", response.url);
        } else {
          updateFormData("photographImage", response.url);
        }
      }
    } catch (error) {
      console.error("Document scanner error:", error);
      alert('Failed to upload scanned document. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, uploadType: "passport" | "photograph") => {
    const file = event.target.files?.[0];
    if (file) {
      setIsProcessing(true);
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Image = reader.result as string;

          // Upload to S3 and get URL
          const response = await visaService.uploadImage(base64Image, uploadType, generatedApplicationId || undefined);

          // Store S3 URL in form data
          if (uploadType === "passport") {
            updateFormData("passportImage", response.url);
          } else {
            updateFormData("photographImage", response.url);
          }
        } catch (error) {
          console.error('Failed to upload image:', error);
          alert('Failed to upload image. Please try again.');
        } finally {
          setIsProcessing(false);
        }
      };
      reader.onerror = () => {
        setIsProcessing(false);
        alert('Failed to read file. Please try again.');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraButtonClick = (uploadType: "passport" | "photograph") => {
    setCurrentUploadType(uploadType);
    if (hasDocumentScanner) {
      startDocumentScanner(uploadType);
    } else {
      setShowFullScreenScanner(true);
    }
  };

  const handleEbusinessPdfUpload = async (documentType: string, file: File) => {
    if (!generatedApplicationId) return;
    setIsProcessing(true);
    try {
      const { url } = await visaService.uploadEbusinessDocument(generatedApplicationId, documentType, file);
      setFormData((prev) => ({
        ...prev,
        ebusinessDocuments: { ...prev.ebusinessDocuments, [documentType]: url },
      }));
    } catch (err) {
      console.error("eBusiness document upload failed:", err);
      alert((t as Record<string, string>).ebusinessUploadError ?? "Failed to upload document. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleScannerCapture = async (imageData: string) => {
    setShowFullScreenScanner(false);
    setIsProcessing(true);

    try {
      // Upload to S3 and get URL
      const response = await visaService.uploadImage(imageData, currentUploadType, generatedApplicationId || undefined);

      // Store S3 URL in form data
      if (currentUploadType === "passport") {
        updateFormData("passportImage", response.url);
      } else {
        updateFormData("photographImage", response.url);
      }
    } catch (error) {
      console.error('Failed to upload captured image:', error);
      alert('Failed to upload captured image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleScannerClose = () => {
    setShowFullScreenScanner(false);
  };

  // Copy application ID to clipboard
  const handleCopyApplicationId = async () => {
    if (!generatedApplicationId) return;
    try {
      await navigator.clipboard.writeText(generatedApplicationId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy application ID:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generatedApplicationId;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  // Validation for each step (step can be 0..9 when isEbusiness, 0..8 otherwise)
  const isStepValid = (step: number): boolean => {
    if (step === 0) return !!formData.visaType;
    if (step === 1) {
      const basicValid = !!(formData.fullName && formData.phoneNumber && formData.email && toE164(formData.phoneCountryCode, formData.phoneNumber));
      if (formData.visaType === "ebusiness_1_year") return basicValid && !!formData.businessTravelPurpose;
      return basicValid;
    }
    if (step === 2) return !!formData.passportImage;
    if (step === 3) return !!formData.photographImage;
    if (step === 4) {
      if (isEbusiness) {
        return requiredEbusinessDocTypes.length > 0 && requiredEbusinessDocTypes.every((dt) => !!formData.ebusinessDocuments[dt]);
      }
      return !!(formData.portOfArrival && formData.expectedArrivalDate && formData.placesIntendedToVisit);
    }
    const travelValid = !!(formData.portOfArrival && formData.expectedArrivalDate && formData.placesIntendedToVisit);
    const familyValid = !!(formData.fatherFullName && formData.fatherPlaceOfBirth && formData.motherFullName && formData.motherPlaceOfBirth && formData.maritalStatus) &&
      (formData.maritalStatus !== "married" || !!(formData.spouseFullName && formData.spouseNationality && formData.spousePlaceOfBirth));
    const addressValid = !!(formData.residentialAddress && formData.educationalQualifications && formData.religion) &&
      (!isEbusiness || !!formData.educationalQualificationInstitute);
    const historyValid = formData.visitedIndiaBefore !== null && formData.visitedSAARCCountries !== null && formData.historyOfOverstaying !== null &&
      formData.previousVisaRejections !== null && formData.refusedEntry !== null && formData.arrestedConvicted !== null &&
      formData.traffickingCrimes !== null && formData.cyberTerrorViolence !== null && formData.glorifyTerrorism !== null && formData.soughtAsylum !== null;
    if (step === 5) return isEbusiness ? travelValid : familyValid;
    if (step === 6) return isEbusiness ? familyValid : addressValid;
    if (step === 7) return isEbusiness ? addressValid : historyValid;
    if (step === 8) return isEbusiness ? historyValid : formData.declarationAccepted;
    if (step === 9) return formData.declarationAccepted;
    return false;
  };

  // Build step payload for saveStep (steps 2-8). Called when leaving current step.
  const getStepPayload = (step: number): VisaApplicationStepData => {
    switch (step) {
      case 2:
        return { passport_image: formData.passportImage ?? undefined };
      case 3:
        return { photograph_image: formData.photographImage ?? undefined };
      case 4:
        return {
          port_of_arrival: formData.portOfArrival,
          expected_arrival_date: formData.expectedArrivalDate,
          places_to_visit: formData.placesIntendedToVisit,
        };
      case 5:
        return {
          father_full_name: formData.fatherFullName,
          father_place_of_birth: formData.fatherPlaceOfBirth,
          mother_full_name: formData.motherFullName,
          mother_place_of_birth: formData.motherPlaceOfBirth,
          marital_status: formData.maritalStatus as 'single' | 'married' | 'other',
          spouse_full_name: formData.spouseFullName || undefined,
          spouse_nationality: formData.spouseNationality || undefined,
          spouse_place_of_birth: formData.spousePlaceOfBirth || undefined,
        };
      case 6:
        return {
          residential_address: formData.residentialAddress,
          educational_qualifications: formData.educationalQualifications,
          religion: formData.religion,
          ...(isEbusiness && formData.educationalQualificationInstitute
            ? { educational_qualification_institute: formData.educationalQualificationInstitute }
            : {}),
        };
      case 7:
        return {
          visited_india_before: formData.visitedIndiaBefore ?? false,
          cities_visited_in_india: formData.countriesVisited || undefined,
          previous_visa_id: formData.previousVisaId || undefined,
          visited_saarc_countries: formData.visitedSAARCCountries ?? false,
          history_of_overstaying: formData.historyOfOverstaying ?? false,
          overstay_explanation: formData.overstayExplanation || undefined,
          previous_visa_rejections: formData.previousVisaRejections ?? false,
          visa_rejection_explanation: formData.visaRejectionExplanation || undefined,
          refused_entry_any_country: formData.refusedEntry ?? false,
          refused_entry_explanation: formData.refusedEntryExplanation || undefined,
          arrested_convicted: formData.arrestedConvicted ?? false,
          trafficking_crimes: formData.traffickingCrimes ?? false,
          cyber_terror_violence: formData.cyberTerrorViolence ?? false,
          glorify_terrorism: formData.glorifyTerrorism ?? false,
          sought_asylum: formData.soughtAsylum ?? false,
        };
      default:
        return {};
    }
  };

  const handleNext = async () => {
    if (!isStepValid(currentStep)) return;

    // Step 0: visa type only — just advance (no save)
    if (currentStep === 0) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
      return;
    }

    // Step 4 eBusiness only: no backend save, just advance
    if (currentStep === 4 && isEbusiness) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
      return;
    }

    // Step 1: create draft application, then advance
    if (currentStep === 1 && !generatedApplicationId) {
      setIsProcessing(true);
      try {
        const draftData = {
          full_name: formData.fullName,
          phone_number: toE164(formData.phoneCountryCode, formData.phoneNumber),
          email: formData.email,
          visa_type: formData.visaType as '30_days' | '1_year' | 'ebusiness_1_year',
          ...(formData.visaType === 'ebusiness_1_year' && formData.businessTravelPurpose
            ? { business_travel_purpose: formData.businessTravelPurpose }
            : {}),
        };
        const response = await visaService.createDraftApplication(draftData);
        setGeneratedApplicationId(response.application_number);
        setApplicationId(response.application_number);
        // Store visa_type from response to avoid unnecessary GET requests later
        if (response.visa_type) {
          setVisaType(response.visa_type);
        }
        setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
      } catch (error) {
        console.error('Failed to create draft application:', error);
        alert('Failed to save your information. Please try again.');
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // Steps 2–8 (backend): save current step and advance
    const backendStep = getBackendStep(currentStep);
    if (backendStep !== undefined && generatedApplicationId && backendStep >= 2 && backendStep <= 8) {
      setIsProcessing(true);
      try {
        const payload = getStepPayload(backendStep);
        await visaService.saveStep(generatedApplicationId, backendStep, payload);
        setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
      } catch (error) {
        console.error('Failed to save step:', error);
        alert('Failed to save your information. Please try again.');
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // Fallback: just advance (e.g. step 1 with existing applicationId)
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!isStepValid(8)) return;
    if (!generatedApplicationId) {
      alert('Application not found. Please go back and complete the form from the start.');
      return;
    }

    setIsProcessing(true);
    try {
      // Save step 8 (declaration) and set status to 'submitted'
      const response = await visaService.saveStep(generatedApplicationId, 8, {
        declaration_accepted: formData.declarationAccepted,
      });
      setApplicationId(response.application_number);
      // Store visa_type from response to avoid unnecessary GET request in PaymentScreen
      if (response.visa_type) {
        setVisaType(response.visa_type);
      }
      setCurrentScreen("apply-payment");
    } catch (error) {
      console.error('Failed to submit application:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Scanner instruction translations
  const scannerInstructions = {
    placePassport: t.placePassport || "Place your document inside the frame",
    moveCloser: t.moveCloser || "Move closer",
    moreLightNeeded: t.moreLightNeeded || "More light needed",
    holdSteady: t.holdSteady || "Hold steady",
    capture: t.capture || "Capture",
  };

  const renderStepContent = () => {
    if (currentStep === 4 && isEbusiness) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {(t as Record<string, string>).ebusinessDocsTitle ?? "Upload supporting documents"}
            </h2>
            <p className="text-muted-foreground">
              {(t as Record<string, string>).ebusinessDocsDesc ?? "Upload PDFs required for your travel purpose."}
            </p>
          </div>
          <div className="space-y-4">
            {requiredEbusinessDocTypes.map((docType) => {
              const label = (t as Record<string, string>)[`doc_${docType}`] ?? docType;
              const hasFile = !!formData.ebusinessDocuments[docType];
              return (
                <div key={docType}>
                  <Label className="text-sm font-medium mb-2 block">{label}</Label>
                  {hasFile ? (
                    <Card className="p-4 bg-primary/10 border-primary flex items-center justify-between">
                      <span className="text-sm font-medium text-primary flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        {(t as Record<string, string>).docUploaded ?? "Uploaded"}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFormData((prev) => {
                              const next = { ...prev.ebusinessDocuments };
                              delete next[docType];
                              return { ...prev, ebusinessDocuments: next };
                            });
                          }}
                        >
                          {t.remove}
                        </Button>
                        <input
                          ref={(el) => { ebusinessFileRefs.current[docType] = el; }}
                          type="file"
                          accept=".pdf,application/pdf"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleEbusinessPdfUpload(docType, f);
                            e.target.value = "";
                          }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => ebusinessFileRefs.current[docType]?.click()}
                        >
                          {(t as Record<string, string>).replace ?? "Replace"}
                        </Button>
                      </div>
                    </Card>
                  ) : (
                    <>
                      <input
                        ref={(el) => { ebusinessFileRefs.current[docType] = el; }}
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleEbusinessPdfUpload(docType, f);
                          e.target.value = "";
                        }}
                      />
                      <Button
                        variant="outline"
                        className="w-full h-12"
                        onClick={() => ebusinessFileRefs.current[docType]?.click()}
                        disabled={isProcessing}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {(t as Record<string, string>).uploadPdf ?? "Upload PDF"}
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    const contentStep = isEbusiness && currentStep >= 5 ? currentStep - 1 : currentStep;
    switch (contentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{t.visaTypeLabel}</h2>
              <p className="text-muted-foreground">{t.visaTypePlaceholder || 'Please choose your visa type'}</p>
            </div>

            <div className="flex flex-col gap-4">
              {[
                { value: "30_days", label: t.visaType30Days, icon: Clock, description: "9900 LKR" },
                { value: "1_year", label: t.visaType1Year, icon: Calendar, description: "14900 LKR" },
                { value: "ebusiness_1_year", label: t.visaTypeEBusiness1Year, icon: Briefcase, description: "15000 LKR" },
              ].map(({ value, label, icon: Icon, description }) => (
                <button
                  key={value}
                  onClick={() => updateFormData("visaType", value)}
                  className={`w-full p-6 rounded-xl border-2 transition-all text-left ${formData.visaType === value
                      ? "bg-primary text-primary-foreground border-primary shadow-lg"
                      : "bg-card text-foreground border-border hover:border-primary/50 hover:bg-secondary"
                    }`}
                  aria-label={label}
                  aria-pressed={formData.visaType === value}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${formData.visaType === value
                          ? "bg-primary-foreground/20"
                          : "bg-muted"
                        }`}>
                        <Icon className={`h-5 w-5 ${formData.visaType === value
                            ? "text-primary-foreground"
                            : "text-muted-foreground"
                          }`} />
                      </div>
                      <div className="text-left">
                        <span className="text-lg font-semibold block">{label}</span>
                        <span className={`text-sm ${formData.visaType === value
                            ? "text-primary-foreground/80"
                            : "text-muted-foreground"
                          }`}>{description}</span>
                      </div>
                    </div>
                    {formData.visaType === value && (
                      <CheckCircle2 className="h-6 w-6 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <FileText className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{t.startApplicationTitle}</h2>
              <p className="text-muted-foreground">{t.startApplicationDesc}</p>
              <div className="mt-4 inline-block bg-muted px-4 py-2 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">
                  {generatedApplicationId
                    ? `${t.applicationId}: ${generatedApplicationId}`
                    : t.applicationIdWillBeGenerated || 'Your application ID will be generated upon submission'}
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="full-name" className="text-sm font-medium mb-2 block">
                {t.fullNameLabel}
              </Label>
              <Input
                id="full-name"
                value={formData.fullName}
                onChange={(e) => updateFormData("fullName", e.target.value)}
                placeholder={t.fullNamePlaceholder}
                className="h-12 text-base"
              />
            </div>

            <div>
              <Label htmlFor="phone-number" className="text-sm font-medium mb-2 block">
                {t.phoneNumberLabel}
              </Label>
              <div className="flex gap-2">
                <Select
                  value={formData.phoneCountryCode}
                  onValueChange={(value) => updateFormData("phoneCountryCode", value)}
                >
                  <SelectTrigger id="phone-country" className="h-12 w-[140px] shrink-0 text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PHONE_COUNTRY_CODES.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="phone-number"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => updateFormData("phoneNumber", e.target.value)}
                  placeholder={t.phoneNumberPlaceholder}
                  className="h-12 flex-1 text-base"
                  inputMode="numeric"
                  autoComplete="tel-national"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {t.phonenumberhelper}
              </p>
            </div>

            <div>
              <Label htmlFor="email-address" className="text-sm font-medium mb-2 block">
                {t.emailAddressLabel}
              </Label>
              <Input
                id="email-address"
                type="email"
                value={formData.email}
                onChange={(e) => updateFormData("email", e.target.value)}
                placeholder={t.emailPlaceholder}
                className="h-12 text-base"
              />
            </div>

            {formData.visaType === "ebusiness_1_year" && (
              <div>
                <Label htmlFor="business-travel-purpose" className="text-sm font-medium mb-2 block">
                  {t.businessTravelPurposeLabel}
                </Label>
                <Select
                  value={formData.businessTravelPurpose}
                  onValueChange={(value) => {
                    updateFormData("businessTravelPurpose", value);
                    const message = (t as Record<string, string>)[`purposePopup_${value}`];
                    if (message) {
                      setPurposePopupMessage(message);
                      setPurposePopupOpen(true);
                    }
                  }}
                >
                  <SelectTrigger id="business-travel-purpose" className="h-12 text-base">
                    <SelectValue placeholder={t.businessTravelPurposePlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {EBUSINESS_PURPOSE_SLUGS.map((slug) => (
                      <SelectItem key={slug} value={slug}>
                        {(t as Record<string, string>)[`purpose_${slug}`] ?? slug}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Camera className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{t.passportMandatory}</h2>
              <p className="text-muted-foreground">{t.uploadDocumentsDesc}</p>
            </div>

            {/* Full-screen Camera Scanner */}
            {showFullScreenScanner && (
              <CameraScanner
                onCapture={handleScannerCapture}
                onClose={handleScannerClose}
                instructions={scannerInstructions}
              />
            )}

            {/* Hidden Inputs */}
            <input
              ref={passportInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleImageUpload(e, "passport")}
              className="hidden"
              id="passport-camera-input"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, currentUploadType)}
              className="hidden"
              id="file-input"
            />

            {/* Passport Upload */}
            <div>
              <Label className="text-base font-bold mb-3 block">{t.passportMandatory}</Label>
              {formData.passportImage ? (
                <Card className="overflow-hidden border-2 border-primary mb-3">
                  <img
                    src={formData.passportImage}
                    alt="Passport"
                    className="w-full aspect-[4/3] object-contain bg-muted"
                  />
                  <div className="p-3 bg-primary/10 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-primary">
                      <Check className="h-5 w-5" />
                      <span className="font-medium">{t.passportUploaded}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateFormData("passportImage", null)}
                      id="retake-passport"
                    >
                      {t.retakePhoto}
                    </Button>
                  </div>
                </Card>
              ) : (
                <>
                  <Card
                    onClick={() => handleCameraButtonClick("passport")}
                    className="p-6 mb-3 cursor-pointer hover:bg-secondary transition-colors border-2 border-dashed border-primary/30 hover:border-primary"
                    id="capture-passport"
                  >
                    <div className="flex flex-col items-center text-center">
                      <Camera className="h-10 w-10 text-primary mb-3" />
                      <p className="font-bold text-foreground">{t.takePhoto}</p>
                    </div>
                  </Card>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setCurrentUploadType("passport");
                      fileInputRef.current?.click();
                    }}
                    id="upload-passport"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {t.uploadFile}
                  </Button>
                </>
              )}
              <p className="text-sm text-muted-foreground mt-2">
                {t.passportValidityNote}<br />
                {t.passportClearNote}
              </p>

              {/* Visual Passport Guide */}
              {!formData.passportImage && (
                <Card className="mt-6 overflow-hidden border-2 border-border">
                  <div className="bg-muted/50 p-4">
                    <p className="text-center font-medium text-foreground mb-3">
                      {t.passportExample || "Position like this:"}
                    </p>
                    <div className="relative aspect-[3/2] bg-card rounded-lg border-2 border-dashed border-primary/40 mx-auto max-w-[280px]">
                      {/* Passport mockup */}
                      <div className="absolute inset-3 flex">
                        {/* Photo area */}
                        <div className="w-1/3 bg-muted rounded flex items-center justify-center">
                          <div className="w-12 h-16 bg-muted-foreground/20 rounded" />
                        </div>
                        {/* Text area */}
                        <div className="flex-1 pl-3 flex flex-col justify-center gap-2">
                          <div className="h-2 bg-muted-foreground/20 rounded w-full" />
                          <div className="h-2 bg-muted-foreground/20 rounded w-4/5" />
                          <div className="h-2 bg-muted-foreground/20 rounded w-3/5" />
                          <div className="h-3 bg-muted-foreground/30 rounded w-full mt-2 font-mono text-[6px] text-muted-foreground flex items-center px-1">
                            {"<<<<<<<<<<<<<<<<<<<<<<"}
                          </div>
                        </div>
                      </div>
                      {/* Corner guides */}
                      <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary rounded-tl" />
                      <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-primary rounded-tr" />
                      <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-primary rounded-bl" />
                      <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary rounded-br" />
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Camera className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{t.photographMandatory}</h2>
              <p className="text-muted-foreground">{t.uploadDocumentsDesc}</p>
            </div>

            {/* Full-screen Camera Scanner */}
            {showFullScreenScanner && (
              <CameraScanner
                onCapture={handleScannerCapture}
                onClose={handleScannerClose}
                instructions={scannerInstructions}
              />
            )}

            {/* Hidden Inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, currentUploadType)}
              className="hidden"
              id="file-input"
            />

            {/* Photograph Upload */}
            <div>
              <Label className="text-base font-bold mb-3 block">{t.photographMandatory}</Label>
              {formData.photographImage ? (
                <Card className="overflow-hidden border-2 border-primary mb-3">
                  <img
                    src={formData.photographImage}
                    alt="Photograph"
                    className="w-full aspect-[3/4] object-contain bg-muted"
                  />
                  <div className="p-3 bg-primary/10 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-primary">
                      <Check className="h-5 w-5" />
                      <span className="font-medium">{t.photographUploaded}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateFormData("photographImage", null)}
                      id="remove-photograph"
                    >
                      {t.remove}
                    </Button>
                  </div>
                </Card>
              ) : (
                <Card
                  onClick={() => {
                    setCurrentUploadType("photograph");
                    fileInputRef.current?.click();
                  }}
                  className="p-8 cursor-pointer hover:bg-secondary transition-colors border-2 border-dashed border-primary/30 hover:border-primary"
                  id="upload-photograph"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Upload className="h-8 w-8 text-primary" />
                    </div>
                    <p className="text-lg font-bold text-foreground mb-2">{t.uploadPhotograph}</p>
                    <p className="text-sm text-muted-foreground">{t.clickToSelectFile}</p>
                  </div>
                </Card>
              )}
              <p className="text-sm text-muted-foreground mt-2">
                {t.photoFormatJpeg}<br />
                {t.photoSizeRequirements}<br />
                {t.photoDimensionRequirements}<br />
                {t.photoWhiteBackgroundNote}
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Plane className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{t.travelDetailsTitle}</h2>
              <p className="text-muted-foreground">{t.travelDetailsDesc}</p>
            </div>

            <div>
              <Label htmlFor="port-of-arrival" className="text-sm font-medium mb-2 block">
                {t.portOfArrivalLabel}
              </Label>
              <Select value={formData.portOfArrival} onValueChange={(value) => updateFormData("portOfArrival", value)}>
                <SelectTrigger id="port-of-arrival" className="h-12 w-full text-base">
                  <SelectValue placeholder={t.portOfArrivalPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {indianPorts.map((port) => (
                    <SelectItem key={port} value={port}>
                      {port}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="arrival-date" className="text-sm font-medium mb-2 block">
                {t.expectedArrivalLabel}
              </Label>
              <Input
                id="arrival-date"
                type="date"
                value={formData.expectedArrivalDate}
                onChange={(e) => updateFormData("expectedArrivalDate", e.target.value)}
                className="h-12 text-base"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <Label htmlFor="places-to-visit" className="text-sm font-medium mb-2 block">
                {t.placesToVisitLabel}
              </Label>
              <Textarea
                id="places-to-visit"
                value={formData.placesIntendedToVisit}
                onChange={(e) => updateFormData("placesIntendedToVisit", e.target.value)}
                placeholder={t.placesToVisitPlaceholder}
                className="min-h-24 text-base"
                rows={3}
              />
              <p className="text-sm text-muted-foreground mt-2">
                {t.placesToVisitHelper}
              </p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{t.personalFamilyTitle}</h2>
              <p className="text-muted-foreground">{t.personalFamilyDesc}</p>
            </div>

            {/* Father's Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-foreground">{t.fatherDetails}</h3>
              <div>
                <Label htmlFor="father-name" className="text-sm font-medium mb-2 block">
                  {t.fatherNameLabel}
                </Label>
                <Input
                  id="father-name"
                  value={formData.fatherFullName}
                  onChange={(e) => updateFormData("fatherFullName", e.target.value)}
                  placeholder={t.fatherNamePlaceholder}
                  className="h-12 text-base"
                />
              </div>
              <div>
                <Label htmlFor="father-birthplace" className="text-sm font-medium mb-2 block">
                  {t.fatherBirthplaceLabel}
                </Label>
                <Input
                  id="father-birthplace"
                  value={formData.fatherPlaceOfBirth}
                  onChange={(e) => updateFormData("fatherPlaceOfBirth", e.target.value)}
                  placeholder={t.fatherBirthplacePlaceholder}
                  className="h-12 text-base"
                />
              </div>
            </div>

            {/* Mother's Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-foreground">{t.motherDetails}</h3>
              <div>
                <Label htmlFor="mother-name" className="text-sm font-medium mb-2 block">
                  {t.motherNameLabel}
                </Label>
                <Input
                  id="mother-name"
                  value={formData.motherFullName}
                  onChange={(e) => updateFormData("motherFullName", e.target.value)}
                  placeholder={t.motherNamePlaceholder}
                  className="h-12 text-base"
                />
              </div>
              <div>
                <Label htmlFor="mother-birthplace" className="text-sm font-medium mb-2 block">
                  {t.motherBirthplaceLabel}
                </Label>
                <Input
                  id="mother-birthplace"
                  value={formData.motherPlaceOfBirth}
                  onChange={(e) => updateFormData("motherPlaceOfBirth", e.target.value)}
                  placeholder={t.motherBirthplacePlaceholder}
                  className="h-12 text-base"
                />
              </div>
            </div>

            {/* Marital Status */}
            <div>
              <Label htmlFor="marital-status" className="text-sm font-medium mb-2 block">
                {t.maritalStatusLabel}
              </Label>
              <Select value={formData.maritalStatus} onValueChange={(value) => updateFormData("maritalStatus", value)}>
                <SelectTrigger id="marital-status" className="h-12 w-full text-base">
                  <SelectValue placeholder={t.maritalStatusPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">{t.single}</SelectItem>
                  <SelectItem value="married">{t.married}</SelectItem>
                  <SelectItem value="other">{t.other}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Spouse Details (conditional) */}
            {formData.maritalStatus === "married" && (
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <h3 className="text-lg font-bold text-foreground">{t.spouseDetails}</h3>
                <div>
                  <Label htmlFor="spouse-name" className="text-sm font-medium mb-2 block">
                    {t.spouseNameLabel}
                  </Label>
                  <Input
                    id="spouse-name"
                    value={formData.spouseFullName}
                    onChange={(e) => updateFormData("spouseFullName", e.target.value)}
                    placeholder={t.spouseNamePlaceholder}
                    className="h-12 text-base"
                  />
                </div>
                <div>
                  <Label htmlFor="spouse-nationality" className="text-sm font-medium mb-2 block">
                    {t.spouseNationalityLabel}
                  </Label>
                  <Input
                    id="spouse-nationality"
                    value={formData.spouseNationality}
                    onChange={(e) => updateFormData("spouseNationality", e.target.value)}
                    placeholder={t.spouseNationalityPlaceholder}
                    className="h-12 text-base"
                  />
                </div>
                <div>
                  <Label htmlFor="spouse-birthplace" className="text-sm font-medium mb-2 block">
                    {t.spouseBirthplaceLabel}
                  </Label>
                  <Input
                    id="spouse-birthplace"
                    value={formData.spousePlaceOfBirth}
                    onChange={(e) => updateFormData("spousePlaceOfBirth", e.target.value)}
                    placeholder={t.spouseBirthplacePlaceholder}
                    className="h-12 text-base"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{t.addressEducationTitle}</h2>
              <p className="text-muted-foreground">{t.addressEducationDesc}</p>
            </div>

            <div>
              <Label htmlFor="residential-address" className="text-sm font-medium mb-2 block">
                {t.residentialAddressLabel}
              </Label>
              <Textarea
                id="residential-address"
                value={formData.residentialAddress}
                onChange={(e) => updateFormData("residentialAddress", e.target.value)}
                placeholder={t.residentialAddressPlaceholder}
                className="min-h-24 text-base"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="education" className="text-sm font-medium mb-2 block">
                {t.educationLabel}
              </Label>
              <Select value={formData.educationalQualifications} onValueChange={(value) => updateFormData("educationalQualifications", value)}>
                <SelectTrigger id="education" className="h-12 w-full text-base">
                  <SelectValue placeholder={t.educationPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {educationLevels.map((level) => (
                    <SelectItem key={level} value={level}>
                      {level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.visaType === "ebusiness_1_year" && (
              <div>
                <Label htmlFor="educational-qualification-institute" className="text-sm font-medium mb-2 block">
                  {(t as Record<string, string>).educationalQualificationInstituteLabel ?? "Qualification acquired from College/University?"}
                </Label>
                <Input
                  id="educational-qualification-institute"
                  value={formData.educationalQualificationInstitute}
                  onChange={(e) => updateFormData("educationalQualificationInstitute", e.target.value.slice(0, 100))}
                  placeholder={(t as Record<string, string>).educationalQualificationInstitutePlaceholder ?? "e.g. University of Colombo"}
                  className="h-12 text-base"
                  maxLength={100}
                />
              </div>
            )}

            <div>
              <Label htmlFor="religion" className="text-sm font-medium mb-2 block">
                {t.religionLabel}
              </Label>
              <Select value={formData.religion} onValueChange={(value) => updateFormData("religion", value)}>
                <SelectTrigger id="religion" className="h-12 w-full text-base">
                  <SelectValue placeholder={t.religionPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {religions.map((religion) => (
                    <SelectItem key={religion} value={religion}>
                      {religion}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Globe className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{t.travelHistoryTitle}</h2>
              <p className="text-muted-foreground">{t.travelHistoryDesc}</p>
            </div>

            {/* Visited India Before */}
            <Card className="p-4">
              <Label className="text-base font-medium mb-3 block">
                {t.visitedIndiaLabel}
              </Label>
              <RadioGroup
                value={formData.visitedIndiaBefore === null ? "" : formData.visitedIndiaBefore.toString()}
                onValueChange={(value) => updateFormData("visitedIndiaBefore", value === "true")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="visited-india-yes" />
                  <Label htmlFor="visited-india-yes" className="font-normal cursor-pointer">{t.yes}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="visited-india-no" />
                  <Label htmlFor="visited-india-no" className="font-normal cursor-pointer">{t.no}</Label>
                </div>
              </RadioGroup>
            </Card>

            {/* Cities Visited in India (conditional) */}
            {formData.visitedIndiaBefore && (
              <>
                <div>
                  <Label htmlFor="cities-visited" className="text-sm font-medium mb-2 block">
                    {t.citiesVisitedInIndiaLabel}
                  </Label>
                  <Textarea
                    id="cities-visited"
                    value={formData.countriesVisited}
                    onChange={(e) => updateFormData("countriesVisited", e.target.value)}
                    placeholder={t.citiesVisitedInIndiaPlaceholder}
                    className="min-h-20 text-base"
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="previous-visa-id" className="text-sm font-medium mb-2 block">
                    {t.previousVisaIdLabel}
                  </Label>
                  <Input
                    id="previous-visa-id"
                    value={formData.previousVisaId}
                    onChange={(e) => updateFormData("previousVisaId", e.target.value)}
                    placeholder={t.previousVisaIdPlaceholder}
                    className="text-base"
                  />
                </div>
              </>
            )}

            {/* Visited SAARC Countries */}
            <Card className="p-4">
              <Label className="text-base font-medium mb-3 block">
                {t.visitedSAARCLabel}
                {t.saarcCountriesList && (
                  <span className="block text-sm font-normal text-muted-foreground mt-1">
                    {t.saarcCountriesList}
                  </span>
                )}
              </Label>
              <RadioGroup
                value={formData.visitedSAARCCountries === null ? "" : formData.visitedSAARCCountries.toString()}
                onValueChange={(value) => updateFormData("visitedSAARCCountries", value === "true")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="saarc-yes" />
                  <Label htmlFor="saarc-yes" className="font-normal cursor-pointer">{t.yes}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="saarc-no" />
                  <Label htmlFor="saarc-no" className="font-normal cursor-pointer">{t.no}</Label>
                </div>
              </RadioGroup>
            </Card>

            {/* History of Overstaying */}
            <Card className="p-4">
              <Label className="text-base font-medium mb-3 block">
                {t.overstayHistoryLabel}
              </Label>
              <RadioGroup
                value={formData.historyOfOverstaying === null ? "" : formData.historyOfOverstaying.toString()}
                onValueChange={(value) => updateFormData("historyOfOverstaying", value === "true")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="overstay-yes" />
                  <Label htmlFor="overstay-yes" className="font-normal cursor-pointer">{t.yes}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="overstay-no" />
                  <Label htmlFor="overstay-no" className="font-normal cursor-pointer">{t.no}</Label>
                </div>
              </RadioGroup>
            </Card>

            {/* Overstay Explanation (conditional) */}
            {formData.historyOfOverstaying === true && (
              <div>
                <Label htmlFor="overstay-explanation" className="text-sm font-medium mb-2 block">
                  {t.overstayExplanationLabel || t.travelExplanationLabel}
                </Label>
                <Textarea
                  id="overstay-explanation"
                  value={formData.overstayExplanation}
                  onChange={(e) => updateFormData("overstayExplanation", e.target.value)}
                  placeholder={t.overstayExplanationPlaceholder || t.travelExplanationPlaceholder}
                  className="min-h-24 text-base"
                  rows={3}
                />
              </div>
            )}

            {/* Previous Visa Rejections */}
            <Card className="p-4">
              <Label className="text-base font-medium mb-3 block">
                {t.visaRejectionsLabel}
              </Label>
              <RadioGroup
                value={formData.previousVisaRejections === null ? "" : formData.previousVisaRejections.toString()}
                onValueChange={(value) => updateFormData("previousVisaRejections", value === "true")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="rejection-yes" />
                  <Label htmlFor="rejection-yes" className="font-normal cursor-pointer">{t.yes}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="rejection-no" />
                  <Label htmlFor="rejection-no" className="font-normal cursor-pointer">{t.no}</Label>
                </div>
              </RadioGroup>
            </Card>

            {/* Visa Rejection Explanation (conditional) */}
            {formData.previousVisaRejections === true && (
              <div>
                <Label htmlFor="visa-rejection-explanation" className="text-sm font-medium mb-2 block">
                  {t.visaRejectionExplanationLabel || t.travelExplanationLabel}
                </Label>
                <Textarea
                  id="visa-rejection-explanation"
                  value={formData.visaRejectionExplanation}
                  onChange={(e) => updateFormData("visaRejectionExplanation", e.target.value)}
                  placeholder={t.visaRejectionExplanationPlaceholder || t.travelExplanationPlaceholder}
                  className="min-h-24 text-base"
                  rows={3}
                />
              </div>
            )}

            {/* Refused Entry */}
            <Card className="p-4">
              <Label className="text-base font-medium mb-3 block">
                {t.refusedEntryLabel}
              </Label>
              <RadioGroup
                value={formData.refusedEntry === null ? "" : formData.refusedEntry.toString()}
                onValueChange={(value) => updateFormData("refusedEntry", value === "true")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="refused-yes" />
                  <Label htmlFor="refused-yes" className="font-normal cursor-pointer">{t.yes}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="refused-no" />
                  <Label htmlFor="refused-no" className="font-normal cursor-pointer">{t.no}</Label>
                </div>
              </RadioGroup>
            </Card>

            {/* Refused Entry Explanation (conditional) */}
            {formData.refusedEntry === true && (
              <div>
                <Label htmlFor="refused-entry-explanation" className="text-sm font-medium mb-2 block">
                  {t.refusedEntryExplanationLabel || t.travelExplanationLabel}
                </Label>
                <Textarea
                  id="refused-entry-explanation"
                  value={formData.refusedEntryExplanation}
                  onChange={(e) => updateFormData("refusedEntryExplanation", e.target.value)}
                  placeholder={t.refusedEntryExplanationPlaceholder || t.travelExplanationPlaceholder}
                  className="min-h-24 text-base"
                  rows={3}
                />
              </div>
            )}

            {/* Arrested / Prosecuted / Convicted */}
            <Card className="p-4">
              <Label className="text-base font-medium mb-3 block">
                {t.arrestedConvictedLabel}
              </Label>
              <RadioGroup
                value={formData.arrestedConvicted === null ? "" : formData.arrestedConvicted.toString()}
                onValueChange={(value) => updateFormData("arrestedConvicted", value === "true")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="arrested-yes" />
                  <Label htmlFor="arrested-yes" className="font-normal cursor-pointer">{t.yes}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="arrested-no" />
                  <Label htmlFor="arrested-no" className="font-normal cursor-pointer">{t.no}</Label>
                </div>
              </RadioGroup>
            </Card>

            {/* Human trafficking / Drug trafficking / etc. */}
            <Card className="p-4">
              <Label className="text-base font-medium mb-3 block">
                {t.traffickingCrimesLabel}
              </Label>
              <RadioGroup
                value={formData.traffickingCrimes === null ? "" : formData.traffickingCrimes.toString()}
                onValueChange={(value) => updateFormData("traffickingCrimes", value === "true")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="trafficking-yes" />
                  <Label htmlFor="trafficking-yes" className="font-normal cursor-pointer">{t.yes}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="trafficking-no" />
                  <Label htmlFor="trafficking-no" className="font-normal cursor-pointer">{t.no}</Label>
                </div>
              </RadioGroup>
            </Card>

            {/* Cyber crime / Terrorist activities / etc. */}
            <Card className="p-4">
              <Label className="text-base font-medium mb-3 block">
                {t.cyberTerrorViolenceLabel}
              </Label>
              <RadioGroup
                value={formData.cyberTerrorViolence === null ? "" : formData.cyberTerrorViolence.toString()}
                onValueChange={(value) => updateFormData("cyberTerrorViolence", value === "true")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="cyber-terror-yes" />
                  <Label htmlFor="cyber-terror-yes" className="font-normal cursor-pointer">{t.yes}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="cyber-terror-no" />
                  <Label htmlFor="cyber-terror-no" className="font-normal cursor-pointer">{t.no}</Label>
                </div>
              </RadioGroup>
            </Card>

            {/* Glorify terrorist violence */}
            <Card className="p-4">
              <Label className="text-base font-medium mb-3 block">
                {t.glorifyTerrorismLabel}
              </Label>
              <RadioGroup
                value={formData.glorifyTerrorism === null ? "" : formData.glorifyTerrorism.toString()}
                onValueChange={(value) => updateFormData("glorifyTerrorism", value === "true")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="glorify-yes" />
                  <Label htmlFor="glorify-yes" className="font-normal cursor-pointer">{t.yes}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="glorify-no" />
                  <Label htmlFor="glorify-no" className="font-normal cursor-pointer">{t.no}</Label>
                </div>
              </RadioGroup>
            </Card>

            {/* Sought asylum */}
            <Card className="p-4">
              <Label className="text-base font-medium mb-3 block">
                {t.soughtAsylumLabel}
              </Label>
              <RadioGroup
                value={formData.soughtAsylum === null ? "" : formData.soughtAsylum.toString()}
                onValueChange={(value) => updateFormData("soughtAsylum", value === "true")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="asylum-yes" />
                  <Label htmlFor="asylum-yes" className="font-normal cursor-pointer">{t.yes}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="asylum-no" />
                  <Label htmlFor="asylum-no" className="font-normal cursor-pointer">{t.no}</Label>
                </div>
              </RadioGroup>
            </Card>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{t.reviewDeclarationTitle}</h2>
              <p className="text-muted-foreground">{t.reviewDeclarationDesc}</p>
            </div>

            {/* Summary */}
            <Card className="p-5 space-y-4">
              <h3 className="text-lg font-bold text-foreground">{t.applicationSummary}</h3>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">{t.personalInformation}</p>
                    <p className="text-sm text-muted-foreground">{formData.fullName}</p>
                    <p className="text-sm text-muted-foreground">{formData.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">{t.documentsUploaded}</p>
                    <p className="text-sm text-muted-foreground">
                      {formData.passportImage ? "✓ " + t.passportMandatory : "✗ " + t.passportMandatory}<br />
                      {formData.photographImage ? "✓ " + t.photographMandatory : "✗ " + t.photographMandatory}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">{t.travelDetails}</p>
                    <p className="text-sm text-muted-foreground">
                      {t.arrival}: {formData.expectedArrivalDate}<br />
                      {t.port}: {formData.portOfArrival}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">{t.personalFamilyDetails}</p>
                    <p className="text-sm text-muted-foreground">
                      {t.father}: {formData.fatherFullName}<br />
                      {t.mother}: {formData.motherFullName}<br />
                      {t.maritalStatusValue}: {formData.maritalStatus}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">{t.additionalInformation}</p>
                    <p className="text-sm text-muted-foreground">
                      {t.educationValue}: {formData.educationalQualifications}<br />
                      {t.religionValue}: {formData.religion}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Declaration */}
            <Card className="p-5">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="declaration-checkbox"
                  checked={formData.declarationAccepted}
                  onCheckedChange={(checked) => updateFormData("declarationAccepted", checked === true)}
                  className="mt-1 border border-muted-foreground/50"
                />
                <Label htmlFor="declaration-checkbox" className="text-sm leading-relaxed cursor-pointer">
                  {t.declarationText}
                </Label>
              </div>
            </Card>

            {!formData.declarationAccepted && (
              <Card className="p-4 bg-muted border-0">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    {t.acceptDeclarationPrompt}
                  </p>
                </div>
              </Card>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)]">
      {/* Progress Indicator */}
      <div className="px-4 py-3 bg-secondary">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">{t.step} {currentStep + 1} {t.of} {totalSteps}</span>
            <span className="text-sm text-muted-foreground">{Math.round(((currentStep + 1) / totalSteps) * 100)}%</span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Application ID Banner - Show on steps 2+ when application ID exists */}
      {generatedApplicationId && currentStep > 1 && (
        <div className="px-3 py-2">
          <div className="max-w-lg mx-auto">
            <Card className="p-4 bg-primary/10 border-primary/20">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    {t.applicationId || "Application ID"}:  <span className="font-mono font-semibold text-primary">{generatedApplicationId}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t.applicationIdCopyPrompt}
                  </p>
                </div>
                <Button
                  onClick={handleCopyApplicationId}
                  variant="outline"
                  size="sm"
                  className="shrink-0 h-9 px-3 border-primary/30 hover:border-primary/50 hover:bg-primary/10"
                  aria-label="Copy application ID"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2 text-primary" />
                      <span className="text-xs">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      <span className="text-xs">Copy</span>
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 px-4 py-6 overflow-y-auto">
        <div className="max-w-lg mx-auto">
          {renderStepContent()}
        </div>
      </div>

      {/* eBusiness travel purpose info popup */}
      <Dialog open={purposePopupOpen} onOpenChange={setPurposePopupOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t.businessTravelPurposePopupTitle}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground whitespace-pre-wrap pt-2">
              {purposePopupMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setPurposePopupOpen(false)}>{t.ok}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <div className="sticky bottom-0 safe-area-inset-bottom">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/95 to-background/80 backdrop-blur-sm border-t border-border/50" />

        {/* Navigation Content */}
        <div className="relative p-4">
          <div className="max-w-lg mx-auto">

            {/* Button Container */}
            <div className="flex gap-3">
              {currentStep > 0 && (
                <Button
                  onClick={handleBack}
                  variant="outline"
                  size="lg"
                  className="h-14 px-6 text-lg font-medium rounded-2xl border-2 border-border/50 hover:border-border hover:bg-muted/50 transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98]"
                  id="back-button"
                >
                  <ArrowLeft className="h-5 w-5 mr-2 transition-transform group-hover:-translate-x-0.5" />
                  {t.back}
                </Button>
              )}

              {/* Spacer for centering when no back button */}
              {currentStep === 0 && <div className="flex-1" />}

              {currentStep < totalSteps - 1 ? (
                <Button
                  onClick={handleNext}
                  disabled={!isStepValid(currentStep) || isProcessing}
                  size="lg"
                  className={`flex-1 h-14 text-xl font-bold rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98] ${isStepValid(currentStep) && !isProcessing
                      ? "bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground"
                      : "bg-muted text-muted-foreground cursor-not-allowed"
                    }`}
                  id="continue-button"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      {t.processing}
                    </>
                  ) : (
                    <>
                      {t.continue}
                      <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!isStepValid(7)}
                  size="lg"
                  className={`flex-1 h-14 text-xl font-bold rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98] ${isStepValid(8)
                      ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
                      : "bg-muted text-muted-foreground cursor-not-allowed"
                    }`}
                  id="submit-button"
                >
                  {t.submitApplication}
                  <CheckCircle2 className="h-5 w-5 ml-2 transition-transform group-hover:scale-110" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
